import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  map,
  of,
  tap,
  takeUntil,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { resolveTenantSubdomainFromHost } from '../../../environments/environment.local.util';
import { AdminAuthStateService } from '../auth/auth.service';
import { AdminWebSocketService, AdminNotificationPayload } from './admin-websocket.service';

/** A persisted admin_alert item returned by GET /alerts/admin-alerts */
export interface AdminAlertItem {
  id: number;
  user_id: number;
  alert_type: string;
  title: string;
  message: string;
  meta_data: Record<string, unknown> | null;
  redirect_url: string | null;
  is_read: boolean;
  created_at: string;
}

interface ApiEnvelope<T> {
  status: boolean;
  message: string;
  data: T;
}

interface AlertsListData {
  allAlerts: AdminAlertItem[];
  totalCount: number;
}

/**
 * Manages admin header bell notifications.
 *
 * - On init: loads unread alerts via REST (GET /alerts/admin-alerts)
 * - Subscribes to AdminWebSocketService `notification` events for real-time prepend
 * - `markRead(id)` → GET /alerts/admin-alerts/:id (marks as read server-side)
 * - `markAllRead()` → POST /alerts/admin-alerts/read-all
 */
@Injectable({ providedIn: 'root' })
export class AdminNotificationService implements OnDestroy {
  private readonly apiBase = environment.apiBaseUrl.replace(/\/$/, '');

  private readonly alerts$ = new BehaviorSubject<AdminAlertItem[]>([]);
  private readonly unreadCount$ = new BehaviorSubject<number>(0);
  private readonly destroy$ = new Subject<void>();
  private liveInitialized = false;
  private notificationStreamSub?: { unsubscribe: () => void };

  get alerts(): Observable<AdminAlertItem[]> {
    return this.alerts$.asObservable();
  }

  get unreadCount(): Observable<number> {
    return this.unreadCount$.asObservable();
  }

  get currentAlerts(): AdminAlertItem[] {
    return this.alerts$.value;
  }

  constructor(
    private readonly http: HttpClient,
    private readonly authState: AdminAuthStateService,
    private readonly wsService: AdminWebSocketService,
  ) {}

  /**
   * Connect WebSocket and load initial unread alerts.
   * Should be called once when the authenticated user is known.
   */
  init(): void {
    const token = this.authState.getToken() ?? undefined;
    if (!token) {
      return; // Not authenticated — do not connect WebSocket or load alerts
    }

    const tenant = this.authState.getTenant();
    const subdomain =
      resolveTenantSubdomainFromHost() ??
      ((tenant as { tenant_subdomain?: string } | null)?.tenant_subdomain ??
        (tenant?.name ? String(tenant.name).toLowerCase() : undefined));

    // Connect WebSocket with auth credentials (idempotent — safe from layout + header)
    this.wsService.connect(token, subdomain);

    if (this.liveInitialized) {
      return;
    }
    this.liveInitialized = true;

    this.notificationStreamSub?.unsubscribe();
    this.notificationStreamSub = this.wsService.onNotification
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: AdminNotificationPayload) => {
        if (payload.is_read) return;
        const newItem: AdminAlertItem = {
          id: payload.id,
          user_id: 0,
          alert_type: payload.alert_type,
          title: payload.title,
          message: payload.message,
          meta_data: null,
          redirect_url: null,
          is_read: false,
          created_at: new Date().toISOString(),
        };
        // Prepend, dedup by id
        const next = [
          newItem,
          ...this.alerts$.value.filter((a) => a.id !== payload.id),
        ];
        this.setList(next);
      });

    // Load initial state
    this.loadNotifications('unread').subscribe();
  }

  /**
   * Read-only fetch of all alerts for the full notifications page.
   * Does NOT mutate the header bell state (unlike `loadNotifications`).
   */
  listAlerts(filter: 'unread' | 'all' | 'read' = 'all'): Observable<AdminAlertItem[]> {
    const params = new HttpParams().set('filter', filter);
    return this.http
      .get<ApiEnvelope<AlertsListData>>(`${this.apiBase}/alerts/admin-alerts`, {
        params,
        withCredentials: true,
      })
      .pipe(
        map((res) => res.data?.allAlerts ?? []),
        catchError(() => of([])),
      );
  }

  /** Load notifications from REST — replaces the current list. */
  loadNotifications(filter: 'unread' | 'all' | 'read' = 'unread'): Observable<AdminAlertItem[]> {
    const params = new HttpParams().set('filter', filter);
    return this.http
      .get<ApiEnvelope<AlertsListData>>(`${this.apiBase}/alerts/admin-alerts`, {
        params,
        withCredentials: true,
      })
      .pipe(
        map((res) => res.data?.allAlerts ?? []),
        tap((items) => this.setList(items.filter((a) => !a.is_read))),
        catchError(() => of([])),
      );
  }

  /**
   * Fetch single alert by id — server marks it as read.
   * Optimistically removes it from the unread list.
   */
  markRead(id: number): Observable<void> {
    this.removeFromList(id);
    return this.http
      .get<ApiEnvelope<AdminAlertItem>>(
        `${this.apiBase}/alerts/admin-alerts/${id}`,
        { withCredentials: true },
      )
      .pipe(
        map(() => undefined),
        catchError(() => {
          // On failure, reload to restore accurate state
          this.loadNotifications('unread').subscribe();
          return of(undefined);
        }),
      );
  }

  /** Mark all unread alerts as read. */
  markAllRead(): Observable<void> {
    this.setList([]);
    return this.http
      .post<ApiEnvelope<unknown>>(
        `${this.apiBase}/alerts/admin-alerts/read-all`,
        {},
        { withCredentials: true },
      )
      .pipe(
        map(() => undefined),
        catchError(() => {
          this.loadNotifications('unread').subscribe();
          return of(undefined);
        }),
      );
  }

  private setList(items: AdminAlertItem[]): void {
    const unread = items.filter((a) => !a.is_read);
    this.alerts$.next(unread);
    this.unreadCount$.next(unread.length);
  }

  private removeFromList(id: number): void {
    this.setList(this.alerts$.value.filter((a) => a.id !== id));
  }

  /** Clears live notification state when the user signs out (root service survives route changes). */
  resetForLogout(): void {
    this.liveInitialized = false;
    this.notificationStreamSub?.unsubscribe();
    this.notificationStreamSub = undefined;
    this.setList([]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
